import { type CustomerContact } from '@utazzo/shared';
import { UzButton, UzConfirmDialog } from '@utazzo/ui';
import { Plus, Trash2, Users } from 'lucide-react';
import React, { useState } from 'react';

import { useRemoveContact } from '../use-customers';

import { ContactFormDialog } from './ContactFormDialog';

interface ContactsStepProps {
  customerId: string;
  contacts: CustomerContact[];
}

/**
 * Step 2 of the customer stepper.
 *
 * Lists secondary contacts (spouse, emergency contact, dependants) already
 * attached to the customer. Each contact can be removed. New contacts are
 * added via a dialog.
 */
export const ContactsStep = ({ customerId, contacts }: ContactsStepProps): React.JSX.Element => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<CustomerContact | null>(null);
  const removeContact = useRemoveContact();

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-zinc-600">
          {contacts.length === 0
            ? 'No contacts yet. Add one below.'
            : `${contacts.length} contact${contacts.length === 1 ? '' : 's'}`}
        </p>
        <UzButton size="sm" onClick={() => setDialogOpen(true)}>
          <Plus className="mr-1.5 h-4 w-4" />
          Add contact
        </UzButton>
      </div>

      {contacts.length > 0 && (
        <ul className="space-y-3">
          {contacts.map((contact) => {
            const fullName = [contact.salutation, contact.firstName, contact.lastName]
              .filter(Boolean)
              .join(' ');
            return (
              <li
                key={contact.id}
                className="bg-background rounded-lg border p-4"
                aria-label={`Contact: ${fullName}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex min-w-0 items-start gap-3">
                    <Users className="mt-0.5 h-4 w-4 shrink-0 text-zinc-400" aria-hidden />
                    <div className="min-w-0 space-y-0.5">
                      <p className="text-sm font-medium text-zinc-900">{fullName}</p>
                      <p className="text-xs text-zinc-500 capitalize">{contact.relationship}</p>
                      {contact.email && <p className="text-xs text-zinc-500">{contact.email}</p>}
                      {contact.mobileCountry && contact.mobileNumber && (
                        <p className="text-xs text-zinc-500">
                          +{contact.mobileCountry} {contact.mobileNumber}
                        </p>
                      )}
                      {contact.notes && (
                        <p className="text-xs text-zinc-400 italic">{contact.notes}</p>
                      )}
                    </div>
                  </div>
                  <UzButton
                    size="sm"
                    variant="ghost"
                    onClick={() => setRemoveTarget(contact)}
                    aria-label={`Remove contact ${fullName}`}
                  >
                    <Trash2 className="h-4 w-4 text-zinc-400" />
                  </UzButton>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <ContactFormDialog open={dialogOpen} onOpenChange={setDialogOpen} customerId={customerId} />

      <UzConfirmDialog
        open={removeTarget !== null}
        onOpenChange={(open) => !open && setRemoveTarget(null)}
        title="Remove this contact?"
        description={
          removeTarget
            ? `${[removeTarget.firstName, removeTarget.lastName].filter(Boolean).join(' ')} (${removeTarget.relationship}) will be removed from this customer.`
            : ''
        }
        confirmLabel="Remove"
        tone="destructive"
        onConfirm={async () => {
          if (removeTarget) {
            await removeContact.mutateAsync({ customerId, contactId: removeTarget.id });
            setRemoveTarget(null);
          }
        }}
      />
    </div>
  );
};
